import React, { useId, useState } from 'react';
import { View } from 'react-native';

import Text from '@/components/Typography/Text';
import { BILLING, PLANS } from '@/constants/pricingContent';
import { MODEL_TOTAL, PROVIDER_TOTAL } from '@/constants/modelInventory.generated';
import {
  COMPARISON_PLANS,
  COMPARISON_ROWS,
} from '@/constants/pricingComparison';
import { PricingButton, PricingSection, PricingTitle, usePricing } from './PricingKit';

const plusPlan = PLANS.find((plan) => plan.id === 'plus');
const gabPlan = COMPARISON_PLANS.find((plan) => plan.featured);
const competitors = COMPARISON_PLANS.filter((plan) => !plan.featured);

/** A real table preserves row/column associations for assistive technology. */
export default function PricingComparison({ onSelectPlan }) {
  const { c, isCompact, isXS, width, pad } = usePricing();
  const comparisonId = useId();
  const [competitorId, setCompetitorId] = useState(competitors[0].id);
  const compareOne = width - pad * 2 < 940;
  const selectedCompetitor = competitors.find((plan) => plan.id === competitorId);
  const visiblePlans = compareOne ? [gabPlan, selectedCompetitor] : COMPARISON_PLANS;
  const cellStyle = {
    padding: isXS ? '18px 12px' : '21px 20px',
    borderBottom: `1px solid ${c.rule}`,
    verticalAlign: 'middle',
    textAlign: 'left',
    fontWeight: 400,
  };
  const featureStyle = {
    ...cellStyle,
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: c.cardSolid,
    borderRight: `1px solid ${c.rule}`,
  };
  const featuredStyle = {
    background: c.accentWash,
    borderLeft: `1px solid ${c.rule}`,
    borderRight: `1px solid ${c.rule}`,
  };

  return (
    <PricingSection label="Compare Gab AI with ChatGPT, Claude, and Grok">
      <View style={{ gap: 16, maxWidth: 740 }}>
        <Text weight="medium" style={{ color: c.muted, fontSize: 14 }}>Compare your options</Text>
        <PricingTitle>More Super Intelligence. One subscription.</PricingTitle>
        <Text tag="p" style={{ color: c.inkSoft, fontSize: 17, lineHeight: 28 }}>
          {`${MODEL_TOTAL} models from ${PROVIDER_TOTAL} providers. Agents for real work. Images, videos, music, and 3D. See how much one Gab AI Plus subscription brings together.`}
        </Text>
      </View>

      <View style={{ flexDirection: isCompact ? 'column' : 'row', justifyContent: 'space-between', gap: 8, marginTop: 32, marginBottom: 14 }}>
        <Text size="s" weight="medium" style={{ color: c.inkSoft }}>Individual plans · Monthly billing · USD</Text>
      </View>

      {compareOne ? (
        <View style={{ gap: 10, marginBottom: 18 }}>
          <Text size="s" weight="medium" style={{ color: c.ink }}>Compare Gab AI with</Text>
          <div role="group" aria-label="Choose a competitor to compare with Gab AI" style={{ display: 'flex', gap: 8 }}>
            {competitors.map((plan) => {
              const selected = plan.id === competitorId;
              return (
                <button
                  key={plan.id}
                  type="button"
                  aria-pressed={selected}
                  aria-controls={comparisonId}
                  onClick={() => setCompetitorId(plan.id)}
                  style={{ flex: 1, minWidth: 0, minHeight: 44, padding: '12px 8px', borderRadius: 10, border: `1px solid ${c.ruleStrong}`, background: selected ? c.ink : c.cardSolid, cursor: 'pointer' }}
                >
                  <Text weight={selected ? 'bold' : 'medium'} style={{ color: selected ? c.page : c.inkSoft }}>{plan.name}</Text>
                </button>
              );
            })}
          </div>
        </View>
      ) : null}

      <div
        style={{ overflow: 'hidden', width: '100%', border: `1px solid ${c.rule}`, borderRadius: 20, background: c.cardSolid }}
      >
        <table
          id={comparisonId}
          aria-label={compareOne ? `Gab AI Plus compared with ${selectedCompetitor.name} ${selectedCompetitor.plan}` : 'Gab AI Plus compared with ChatGPT Plus, Claude Pro, and SuperGrok'}
          style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, color: c.ink }}
        >
          <colgroup>
            {!compareOne ? <col style={{ width: '26%' }} /> : null}
            {visiblePlans.map((plan) => <col key={plan.id} />)}
          </colgroup>
          <thead>
            <tr>
              {!compareOne ? <th scope="col" style={featureStyle}>
                <Text weight="medium" style={{ color: c.inkSoft }}>What you get</Text>
              </th> : null}
              {visiblePlans.map((plan) => (
                <th key={plan.id} id={`${comparisonId}-${plan.id}`} scope="col" style={{ ...cellStyle, ...(plan.featured ? featuredStyle : {}), verticalAlign: 'top', borderTop: `3px solid ${plan.featured ? c.accent : 'transparent'}` }}>
                  <View>
                    <Text weight="bold" style={{ color: plan.featured ? c.accent : c.ink, fontSize: 19 }}>{plan.name}</Text>
                    <Text size="s" style={{ color: c.muted, marginTop: 4 }}>{plan.plan}</Text>
                    <Text weight="semibold" style={{ color: c.ink, fontSize: 29, letterSpacing: -1, marginTop: 18 }}>
                      ${Number(plan.monthly).toLocaleString('en-US')}
                      <Text size="s" style={{ color: c.muted, letterSpacing: 0 }}> / mo</Text>
                    </Text>
                  </View>
                </th>
              ))}
            </tr>
          </thead>
          {COMPARISON_ROWS.map((row, index) => (
            <tbody key={row.label}>
              {compareOne ? (
                <tr>
                  <th id={`${comparisonId}-feature-${index}`} scope="rowgroup" colSpan={2} style={{ ...cellStyle, padding: '12px', background: c.panel }}>
                    <Text size="s" weight="bold" style={{ color: c.ink, lineHeight: 20 }}>{row.label}</Text>
                  </th>
                </tr>
              ) : null}
              <tr>
                {!compareOne ? <th id={`${comparisonId}-feature-${index}`} scope="row" style={featureStyle}>
                  <Text weight="medium" style={{ color: c.ink, lineHeight: 21 }}>{row.label}</Text>
                </th> : null}
                {visiblePlans.map((plan) => {
                  const cell = row.cells[plan.id];
                  return (
                    <td key={plan.id} headers={`${comparisonId}-feature-${index} ${comparisonId}-${plan.id}`} style={{ ...cellStyle, ...(plan.featured ? featuredStyle : {}), ...(compareOne ? { verticalAlign: 'top' } : {}) }}>
                      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
                        {cell.included ? <span aria-hidden="true" style={{ color: plan.featured ? c.accent : c.muted, lineHeight: '21px' }}>✓</span> : null}
                        <View style={{ flex: 1, minWidth: 0, gap: 5 }}>
                          <Text size="s" weight={plan.featured ? 'bold' : 'normal'} style={{ color: plan.featured ? c.accent : c.inkSoft, lineHeight: 21 }}>{cell.label}</Text>
                          {cell.detail ? <Text size="xs" style={{ color: c.muted, lineHeight: 18 }}>{cell.detail}</Text> : null}
                        </View>
                      </View>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          ))}
        </table>
      </div>

      <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'stretch' : 'center', justifyContent: 'space-between', gap: 24, marginTop: 32, padding: isXS ? 22 : 28, borderWidth: 1, borderColor: c.rule, borderRadius: 20, backgroundColor: c.accentWash }}>
        <View style={{ flex: isCompact ? undefined : 1, gap: 8 }}>
          <Text weight="semibold" style={{ color: c.ink, fontSize: 21 }}>One place to work. More ways to create.</Text>
          <Text style={{ color: c.inkSoft, lineHeight: 22 }}>
            {`Gab AI Plus is $${BILLING.monthly}/month, or $${BILLING.annualPerMonth}/month billed annually ($${BILLING.annualTotal}/year).`}
          </Text>
        </View>
        <PricingButton label="Get Gab AI Plus" full={isCompact} onPress={onSelectPlan ? () => onSelectPlan(plusPlan) : undefined} />
      </View>
    </PricingSection>
  );
}
